import { useLogger } from '#layers/base/composables/useLogger'

interface Page<T> {
  items: T[]
}

export interface CategoryGroupType {
  handle: string
  i18nKey: string
  description: Record<string, string>
}

type SubCategoryGroup = CategoryGroupType

export const useCategoryGroup = defineStore('category-group', () => {
  const { baseURL } = useRuntimeConfig().app
  const log = useLogger('useCategoryGroup')

  const {
    data: categoryGroupsData,
    pending: isCategoryGroupsPending,
    refresh: refreshCategoryGroups,
    error: categoryGroupsError,
  } = useFetch<Page<CategoryGroupType>>('/api/easyApi/categories', {
    baseURL,
    server: false,
    immediate: true,
    onResponseError({ error }) {
      log.warn('Failed to fetch category groups', { error })
    },
  })

  const categoriesHandle = ref<string>('')
  const subCategoriesHandle = ref<string>('')

  const {
    data: categoriesData,
    pending: isCategoriesPending,
    refresh: refreshCategories,
    error: categoriesError,
  } = useFetch<Page<CategoryGroupType>>(
    () => `/api/easyApi/categories?gHandle=${categoriesHandle.value}`,
    {
      baseURL,
      key: () => `categories-${categoriesHandle.value}`,
      watch: [categoriesHandle],
      server: false,
      lazy: true,
      immediate: false,
      onResponseError({ error }) {
        log.warn('Failed to fetch categories for handle', { handle: categoriesHandle.value, error })
      },
    })

  const {
    data: subCategoriesData,
    pending: isSubCategoriesPending,
    refresh: refreshSubCategories,
    error: subCategoriesError,
  } = useFetch<Page<SubCategoryGroup>>(
    () => `/api/easyApi/categories?cHandle=${subCategoriesHandle.value}`,
    {
      baseURL,
      key: () => `subcategories-${subCategoriesHandle.value}`,
      watch: [subCategoriesHandle],
      server: false,
      lazy: true,
      immediate: false,
      onResponseError({ error }) {
        log.warn('Failed to fetch subcategories for handle', { handle: subCategoriesHandle.value, error })
      },
    })

  const categoryGroup = computed(() => categoryGroupsData.value?.items ?? [])
  const categories = computed(() => categoriesData.value?.items ?? [])
  const subCategories = computed(() => subCategoriesData.value?.items ?? [])

  function getCategoryGroups() {
    refreshCategoryGroups()
  }

  function getCategories(handle: string) {
    categoriesHandle.value = handle
  }

  function getSubCategories(handle: string) {
    subCategoriesHandle.value = handle
  }

  function $reset() {
    categoriesHandle.value = ''
    subCategoriesHandle.value = ''
    refreshCategoryGroups()
  }

  return {
    // Computed data
    categoryGroup,
    categories,
    subCategories,

    // Pending states
    isCategoryGroupsPending,
    isCategoriesPending,
    isSubCategoriesPending,

    // Errors
    categoryGroupsError,
    categoriesError,
    subCategoriesError,

    // Functions
    getCategoryGroups,
    getCategories,
    getSubCategories,
    refreshCategoryGroups,
    refreshCategories,
    refreshSubCategories,
    $reset,
  }
})
